import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './TaskPage.css';
import './DailyRewardPopup.css';
import { useAuth } from '../contexts/AuthContext';
import { TaskCard, NotificationModal, BaseLayout } from '../components';
import { taskApi } from '../services/taskApi';
import { TaskProgress } from '../types';

const TaskPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<TaskProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'all' | 'one_time' | 'daily' | 'time_limited'>('all');
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    title: '',
    message: '',
    icon: '/images/icons/settings.svg',
    type: 'info' as 'success' | 'error' | 'warning' | 'info'
  });
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(0);

  const handleGoToKilohub = () => {
    setShowRewardPopup(false);
    navigate('/kiloba-hub'); // Assuming '/kilohub' is the correct path
  };

  const handleCloseRewardPopup = () => {
    setShowRewardPopup(false);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  // Show notification helper
  const showNotificationMessage = (
    title: string, 
    message: string, 
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    icon?: string
  ) => {
    setNotificationData({ 
      title, 
      message, 
      icon: icon || '/images/icons/settings.svg',
      type 
    });
    setShowNotification(true);
  };

  // Fetch tasks from API
  const fetchTasks = async (type?: 'one_time' | 'daily' | 'time_limited') => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (type) {
        response = await taskApi.getUserTasksByType(type);
      } else {
        response = await taskApi.getUserTasks();
      }
      
      if (response.success && response.data) {
        setTasks(response.data);
      } else {
        setError(response.message || 'Không thể tải danh sách nhiệm vụ');
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Đã xảy ra lỗi khi tải danh sách nhiệm vụ');
    } finally {
      setLoading(false);
    }
  };

  // Handle task completion
  const handleCompleteTask = async (taskId: number) => {
    try {
      // Find the task to determine how to complete it
      const taskProgress = tasks.find(t => t.task.id === taskId);
      if (!taskProgress) return;

      const { task } = taskProgress;
      
      // Special handling for profile_update and phone_verification tasks
      if (task.category === 'profile_update' || task.category === 'phone_verification') {
        // showNotificationMessage(
        //   'Thông báo', 
        //   'Hãy truy cập vào mục Cài đặt để cập nhật thông tin',
        //   'info',
        //   '/images/icons/settings.svg'
        // );
        navigate('/settings?fromTask=true');
        return;
      }

      // Special handling for mini_quiz tasks - navigate to MiniQuizPage
      if (task.category === 'mini_quiz') {
        navigate('/mini-quiz?fromTask=true');
        return;
      }
      
      let response;
      if (task.type === 'daily') {
        response = await taskApi.completeDailyTask(task.category);
      } else {
        response = await taskApi.completeOneTimeTask(taskId);
      }

      if (response.success) {
        // Show success message
        showNotificationMessage(
          'Thành công!', 
          'Hoàn thành nhiệm vụ thành công!',
          'success'
        );
        // Refresh tasks after completion
        fetchTasks(selectedTab === 'all' ? undefined : selectedTab);
      } else {
        showNotificationMessage(
          'Thông báo', 
          response.error || 'Không thể hoàn thành nhiệm vụ',
          'info'
        );
      }
    } catch (err) {
      showNotificationMessage(
        'Lỗi', 
        'Đã xảy ra lỗi khi hoàn thành nhiệm vụ',
        'error'
      );
    }
  };

  // Handle claim reward
  const handleClaimReward = async (taskId: number) => {
    try {
      const taskProgress = tasks.find(t => t.task.id === taskId);
      if (!taskProgress) return;

      const response = await taskApi.claimReward(taskId);
      
      if (response.success) {
        // Set reward amount and show unified popup
        setRewardAmount(response.data?.rewardEnergy || 0);
        setShowRewardPopup(true);
        
        // Refresh tasks after claiming
        fetchTasks(selectedTab === 'all' ? undefined : selectedTab);
      } else {
        showNotificationMessage(
          'Lỗi', 
          response.error || 'Không thể nhận thưởng',
          'error'
        );
      }
    } catch (err) {
      showNotificationMessage(
        'Lỗi', 
        'Đã xảy ra lỗi khi nhận thưởng',
        'error'
      );
    }
  };

  // Handle tab change
  const handleTabChange = (tab: typeof selectedTab) => {
    setSelectedTab(tab);
    fetchTasks(tab === 'all' ? undefined : tab);
  };

  // Load tasks on component mount
  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [user]);

  return (
    <div className="viewport-wrapper">
      <BaseLayout className="common-container" backgroundColor="transparent">
        <div className="task-page">
          <div className='task-header'>
            <button className="task-back-btn" onClick={handleBackClick}>
              <img src="/images/icons/back.svg" alt="Back" />
            </button>
            <div className="task-title">
              <span>Nhiệm vụ</span>
            </div>
          </div>
          
          <div className="task-content">
            {/* Loading state */}
            {loading && (
              <div className="task-loading">
                <div className="loading-spinner"></div>
                <p>Đang tải nhiệm vụ...</p>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="task-error">
                <p>{error}</p>
                <button onClick={() => fetchTasks(selectedTab === 'all' ? undefined : selectedTab)}>
                  Thử lại
                </button>
              </div>
            )}

            {/* Tasks list */}
            {!loading && !error && (
              <div className="task-list">
                {tasks.length === 0 ? (
                  <div className="task-empty">
                    <p>Không có nhiệm vụ nào</p>
                  </div>
                ) : (
                  tasks.map((taskProgress) => (
                    <TaskCard
                      key={taskProgress.task.id}
                      taskProgress={taskProgress}
                      onComplete={handleCompleteTask}
                      onClaimReward={handleClaimReward}
                    />
                  ))
                )}
              </div>
            )}
          </div>

          {/* Notification Modal */}
          <NotificationModal
            isOpen={showNotification}
            onClose={() => setShowNotification(false)}
            title={notificationData.title}
            message={notificationData.message}
            icon={notificationData.icon}
            type={notificationData.type}
          />

          {/* Reward Popup */}
          {showRewardPopup && (
            <div className="daily-reward-popup-overlay">
              <div className="daily-reward-popup">
                <h3>Chúc mừng!</h3>
                <p>Bạn đã nhận được +{rewardAmount} điểm năng lượng!</p>
                <p>Hãy truy cập KILOHUB để nâng cấp KILOBA của bạn nhé!</p>
                <div className="daily-reward-popup-buttons">
                  <button onClick={handleGoToKilohub} className="btn-primary">Đến KILOHUB</button>
                  <button onClick={handleCloseRewardPopup} className="btn-secondary">Để sau</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </BaseLayout>
    </div>
  );
};

export default TaskPage;
